import AsyncStorage from "@react-native-async-storage/async-storage";

const STORAGE_KEY = "app_messages_v1";

/** @typedef {'success_notification'|'fail_notification'|'system'} AppMessageType */

/**
 * @typedef {Object} AppMessage
 * @property {string} id
 * @property {AppMessageType} type
 * @property {string} dateKey
 * @property {string} title
 * @property {string} body
 * @property {boolean} read
 * @property {boolean} [needsFailDetails]
 * @property {boolean} [failDetailsCompleted]
 * @property {string} createdAt
 */

/**
 * @returns {Promise<AppMessage[]>}
 */
export async function loadAppMessages() {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {AppMessage[]} messages
 */
async function saveAppMessages(messages) {
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
}

/**
 * @param {Omit<AppMessage, 'id'|'createdAt'|'read'> & { read?: boolean }} partial
 */
export async function addAppMessage(partial) {
  const messages = await loadAppMessages();
  const duplicate = messages.find(
    (m) =>
      m.type === partial.type &&
      m.dateKey === partial.dateKey &&
      partial.type === "fail_notification" &&
      m.needsFailDetails &&
      !m.failDetailsCompleted,
  );
  if (duplicate) return duplicate;

  const msg = {
    id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    read: false,
    createdAt: new Date().toISOString(),
    ...partial,
  };
  await saveAppMessages([msg, ...messages]);
  return msg;
}

/**
 * @returns {Promise<number>}
 */
export async function getUnreadMessageCount() {
  const messages = await loadAppMessages();
  return messages.filter((m) => !m.read || (m.needsFailDetails && !m.failDetailsCompleted)).length;
}

/**
 * @param {string} id
 */
export async function markAppMessageRead(id) {
  const messages = await loadAppMessages();
  const next = messages.map((m) => (m.id === id ? { ...m, read: true } : m));
  await saveAppMessages(next);
}

/**
 * @param {string} dateKey
 */
export async function completeFailDetailsForDate(dateKey) {
  const messages = await loadAppMessages();
  const next = messages.map((m) =>
    m.type === "fail_notification" && m.dateKey === dateKey
      ? { ...m, read: true, failDetailsCompleted: true, needsFailDetails: false }
      : m,
  );
  await saveAppMessages(next);
}

/**
 * @param {string} dateKey
 * @returns {Promise<AppMessage|null>}
 */
export async function getPendingFailMessage(dateKey) {
  const messages = await loadAppMessages();
  return (
    messages.find(
      (m) => m.type === "fail_notification" && m.dateKey === dateKey && m.needsFailDetails && !m.failDetailsCompleted,
    ) || null
  );
}
